import React from 'react';
import { Box, TextField as MuiTextField, InputAdornment, IconButton } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUpRightFromSquare } from '@fortawesome/pro-light-svg-icons';
import { BackgroundInformationModel, ServiceProviderUnitModel, CompanyLookupModel } from '../../../../api-service/clients';
import { DeepPartial, ServiceProviderAssignmentErrors } from './interfaces';
import { Cell } from '../../../../components/cell';
import { Row } from '../../../../components/row';
import { SingleSelect } from '../../../../components/single-select';
import { MaxLengthTextField, TextField } from '../../../../components/text-field';
import { CompanyLookup } from '../../../../components/lookups/company-lookup/company-lookup';
import { UserAndTeamLookup } from '../../../offer-and-activity/components/user-and-team-lookup';
import { YesNoComponent, YesNo } from '../../../../components/radio-group/yes-no-component';
import { GuardianTypeComponent } from '../../authority/assessment/components/guardian-type-component';
import {
  fromGuardianTypeModel,
  toGuardianTypeModel,
  GuardianTypeValue,
  fromRepresentationTypeModel,
  toRepresentationTypeModel,
  RepresentationTypeValue,
} from '../../authority/assessment/components/utils';
import { RepresentationTypeComponent } from '../../authority/assessment/components/representation-type-component';
import { Editor, IEditorRef } from '../../../../components/editor';

interface IBackgroundInformationProps {
  backgroundInformation: DeepPartial<BackgroundInformationModel>;
  isReadOnly: boolean;
  lockServiceProviderUnit: boolean;
  errors: Pick<ServiceProviderAssignmentErrors, 'name' | 'companyId' | 'serviceProviderUnitId' | 'responsibleUserId'>;
  serviceProviderUnits: ServiceProviderUnitModel[];
  activityHasSpu: boolean;
  activityHasCompany: boolean;
  hideActivityField?: boolean;
  companyReadOnly?: boolean;
  spuReadOnly?: boolean;
  onNameChange: (value: string) => void;
  onCompanyChange: (value?: CompanyLookupModel) => void;
  onSocialProviderUnitChanged: (value?: ServiceProviderUnitModel) => void;
  onResponsibleUserChange: (userId?: string, teamId?: string) => void;
  onRichTextChange: (section: 'backgroundInformation' | 'coordinationDetails' | 'assessmentInformation', fieldName: string, event: unknown, editor: unknown) => void;
  onHasGuardianChange: (value: YesNo) => void;
  onGuardianTypeChange: (value?: GuardianTypeValue) => void;
  onGuardianDetailsChange: (value: string) => void;
  onIsRepresentedChange: (value: YesNo) => void;
  onRepresentationTypeChange: (value?: RepresentationTypeValue) => void;
  onPowersOfAttorneyChange: (value: string) => void;
  activityTitle?: string;
  onOpenActivity?: () => void;
  showActivityLink: boolean;
  isSocialProviderContext?: boolean;
}

export function BackgroundInformation(props: IBackgroundInformationProps) {
  const {
    backgroundInformation,
    isReadOnly,
    lockServiceProviderUnit,
    errors,
    serviceProviderUnits,
    activityHasSpu,
    activityHasCompany,
    hideActivityField = false,
    companyReadOnly = false,
    spuReadOnly = false,
    onNameChange,
    onCompanyChange,
    onSocialProviderUnitChanged,
    onResponsibleUserChange,
    onRichTextChange,
    onHasGuardianChange,
    onGuardianTypeChange,
    onGuardianDetailsChange,
    onIsRepresentedChange,
    onRepresentationTypeChange,
    onPowersOfAttorneyChange,
    activityTitle,
    onOpenActivity,
    showActivityLink,
    isSocialProviderContext = false,
  } = props;

  const responsibleUserLabel = isSocialProviderContext ? 'Ansvarlig medarbejder' : 'Ansvarlig sagsbehandler';

  const {
    name,
    companyId,
    responsibleUserId,
    responsibleTeamId,
    citizenMedicalDiagnosesRichText,
    specialInformationAboutCitizenRichText,
    hasGuardian,
    guardianType,
    guardianDetails,
    isRepresented,
    representationType,
    powersOfAttorney,
  } = backgroundInformation || {};

  const citizenMedicalDiagnosesEditorRef = React.useRef<IEditorRef>(null);
  const specialInformationEditorRef = React.useRef<IEditorRef>(null);

  function getYesNoFromBoolean(value?: boolean): YesNo {
    if (value === true) return YesNo.Yes;
    if (value === false) return YesNo.No;
    return YesNo.None;
  }

  function handleGuardianTypeChange(_name: string, value?: GuardianTypeValue) {
    onGuardianTypeChange(toGuardianTypeModel(value));
  }

  function handleRepresentationTypeChange(_name: string, value?: RepresentationTypeValue) {
    onRepresentationTypeChange(toRepresentationTypeModel(value));
  }

  const selectedSpu = serviceProviderUnits.find(x => x.serviceProviderUnitId?.value === backgroundInformation?.serviceProviderUnitId?.value);
  const hasGuardianYesNo = getYesNoFromBoolean(hasGuardian);
  const isRepresentedYesNo = getYesNoFromBoolean(isRepresented);

  const showActivity = !hideActivityField && showActivityLink && activityTitle;
  return (
    <Box mt={1}>
      <Row>
        <Cell size={showActivity ? 'half' : 'full'}>
          <TextField
            label="Navn"
            variant="outlined"
            value={name}
            error={!!errors.name}
            errorText={errors.name}
            disabled={isReadOnly}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => onNameChange(event.currentTarget.value)}
          />
        </Cell>
        {showActivity && (
          <Cell size="half">
            <MuiTextField
              label="Aktivitet"
              value={activityTitle}
              variant="outlined"
              fullWidth
              InputProps={{
                readOnly: true,
                startAdornment: onOpenActivity ? (
                  <InputAdornment position="start">
                    <IconButton size="small" onClick={onOpenActivity}>
                      <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
                    </IconButton>
                  </InputAdornment>
                ) : undefined,
              }}
            />
          </Cell>
        )}
      </Row>
      <Box mt={1}>
        <Row>
          <Cell size="half">
            {companyId &&
              <CompanyLookup value={companyId.value} onChange={onCompanyChange} label="Virksomhed" required readOnly={isReadOnly || activityHasCompany || companyReadOnly} />}
          </Cell>
          <Cell size="half">
            <SingleSelect<ServiceProviderUnitModel>
              label="Udførerenhed"
              getOptionLabel={(option: ServiceProviderUnitModel) => option.name ?? ''}
              value={selectedSpu}
              options={serviceProviderUnits}
              renderOption={(prop: object, option: ServiceProviderUnitModel) => (
                <li {...prop} key={option.serviceProviderUnitId?.value ?? ''}>
                  {option.name}
                </li>
              )}
              onChange={onSocialProviderUnitChanged}
              variant="outlined"
              error={!!errors.serviceProviderUnitId}
              errorText={errors.serviceProviderUnitId}
              disabled={lockServiceProviderUnit || isReadOnly || activityHasSpu || spuReadOnly}
            />
          </Cell>
        </Row>
      </Box>
      <Box mt={1}>
        <UserAndTeamLookup
          userProps={{
            label: responsibleUserLabel,
            value: responsibleUserId?.value,
            onChange: userId => {
              onResponsibleUserChange(userId, responsibleTeamId?.value);
            },
            error: !!errors.responsibleUserId,
            errorText: errors.responsibleUserId,
            disabled: isReadOnly,
            required: true,
          }}
          teamProps={{
            label: 'Team',
            value: responsibleTeamId?.value,
            onChange: teamId => {
              onResponsibleUserChange(responsibleUserId?.value, teamId);
            },
            disabled: isReadOnly,
          }}
          disabled={isReadOnly}
          render={(userInput, teamInput) => (
            <Row>
              <Cell size="half">{userInput}</Cell>
              <Cell size="half">{teamInput}</Cell>
            </Row>
          )}
        />
      </Box>
      <Box mt={1}>
        <Row>
          <Cell size="full">
            <Editor
              label="Borgers eventuelle lægefaglige diagnoser"
              value={citizenMedicalDiagnosesRichText}
              initialValue={citizenMedicalDiagnosesRichText}
              ref={citizenMedicalDiagnosesEditorRef}
              onChange={(event, editor) => onRichTextChange('backgroundInformation', 'citizenMedicalDiagnoses', event, editor)}
              onBlur={(event, editor) => onRichTextChange('backgroundInformation', 'citizenMedicalDiagnoses', event, editor)}
              resizable
              selectorPrefix="assignment-citizen-medical-diagnoses-"
              disabled={isReadOnly}
            />
          </Cell>
        </Row>
      </Box>
      <Box mt={1}>
        <Row>
          <Cell size="full">
            <Editor
              label="Særlige oplysninger om borgeren"
              value={specialInformationAboutCitizenRichText}
              initialValue={specialInformationAboutCitizenRichText}
              ref={specialInformationEditorRef}
              onChange={(event, editor) => onRichTextChange('backgroundInformation', 'specialInformationAboutCitizen', event, editor)}
              onBlur={(event, editor) => onRichTextChange('backgroundInformation', 'specialInformationAboutCitizen', event, editor)}
              resizable
              selectorPrefix="assignment-special-information-"
              disabled={isReadOnly}
            />
          </Cell>
        </Row>
      </Box>
      <Box mt={1}>
        <Row>
          <Cell size="half">
            <YesNoComponent header="Har borger en værge?" value={hasGuardianYesNo} onChange={value => onHasGuardianChange(value)} readOnly={isReadOnly} />
          </Cell>
          <Cell size="half">
            {hasGuardianYesNo === YesNo.Yes && (
              <GuardianTypeComponent
                guardianType={fromGuardianTypeModel(guardianType)}
                hasGuardian={hasGuardian === true}
                isReadOnly={isReadOnly}
                onGuardianTypeChange={handleGuardianTypeChange}
              />
            )}
          </Cell>
        </Row>
        {hasGuardianYesNo === YesNo.Yes && (
          <Row>
            <Cell size="full">
              <Box mt={1}>
                <MaxLengthTextField
                  value={guardianDetails || ''}
                  variant="outlined"
                  maxLength={10000}
                  multiline
                  label="Detaljer vedrørende værgemål"
                  fullWidth
                  rows={5}
                  onChange={e => onGuardianDetailsChange(e.currentTarget.value)}
                  disabled={isReadOnly}
                />
              </Box>
            </Cell>
          </Row>
        )}
      </Box>
      <Box mt={1}>
        <Row>
          <Cell size="half">
            <YesNoComponent header="Er borgeren repræsenteret?" value={isRepresentedYesNo} onChange={value => onIsRepresentedChange(value)} readOnly={isReadOnly} />
          </Cell>
          <Cell size="half">
            {isRepresentedYesNo === YesNo.Yes && (
              <RepresentationTypeComponent
                representationType={fromRepresentationTypeModel(representationType)}
                isRepresented={isRepresented === true}
                isReadOnly={isReadOnly}
                onRepresentationTypeChange={handleRepresentationTypeChange}
              />
            )}
          </Cell>
        </Row>
      </Box>
      <Box mt={1}>
        <Row>
          <Cell size="full">
            <MaxLengthTextField
              value={powersOfAttorney || ''}
              variant="outlined"
              maxLength={10000}
              multiline
              label="Eventuelle fuldmagter"
              fullWidth
              rows={5}
              onChange={e => onPowersOfAttorneyChange(e.currentTarget.value)}
              disabled={isReadOnly}
            />
          </Cell>
        </Row>
      </Box>
    </Box>
  );
}
